import { piloteerApi } from '@/api/piloteer';
import { configureStore } from '@reduxjs/toolkit';
import type { AnyAction } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import * as Sentry from '@sentry/react';

import { authReducer } from './auth/slice';
import { multiTenantReducer } from './multi-tenant/slice';

const SENTRY_REDACTED = '[Filtered]';
const SENTRY_SENSITIVE_KEYS = new Set([
  'accessToken',
  'access_token',
  'idToken',
  'id_token',
  'refreshToken',
  'refresh_token',
]);

const redactSentryValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(redactSentryValue);
  }
  if (!value || typeof value !== 'object') {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, nestedValue]) => [
      key,
      SENTRY_SENSITIVE_KEYS.has(key)
        ? SENTRY_REDACTED
        : redactSentryValue(nestedValue),
    ])
  );
};

const redactSentryAction = (action: AnyAction): AnyAction =>
  redactSentryValue(action) as AnyAction;

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  attachReduxState: false,
  actionTransformer: redactSentryAction,
});

export const store = configureStore({
  reducer: {
    [piloteerApi.reducerPath]: piloteerApi.reducer,
    auth: authReducer,
    multiTenant: multiTenantReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(piloteerApi.middleware),
  enhancers: (getDefaultEnhancers) =>
    getDefaultEnhancers().concat(sentryReduxEnhancer),
});

setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
